import type {
  ChartConfig,
  ChartDataModel,
  Entitlements,
  IChartPlugin,
  OptionSchema,
  RenderContext
} from '@chartforge/shared-types';
import {
  ChartValidationError,
  cloneConfig,
  createDefaultConfig,
  renderChart,
  updateConfig,
  watermarkForEntitlements
} from '@chartforge/engine-core';
import type { ClientTransport } from '../bridge/client-transport';
import {
  changedOptionIds,
  createMessage,
  parseMessage,
  PROTOCOL_VERSION,
  protocolErrorPayload,
  type BridgeMessage,
  type ClientToHostKind,
  type EntitlementSource,
  type HostInitPayload,
  type PayloadFor,
  type SelectionReference
} from '../protocol/messages';

/**
 * Client-side controller for the Sheets sidebar.
 *
 * ```text
 * host:init  -> config (defaults from OptionSchema + persisted values) + entitlements
 * host:model -> ChartDataModel -> engine renderChart (validate | render | watermark | resize)
 * settings   -> updateConfig (engine, schema-validated) -> client:config:change
 * ```
 *
 * All chart behaviour stays in `engine-core` + the `timeline` plugin: this class
 * only wires the bridge to the shared render pipeline, exactly like the Excel
 * (M1) and Power BI (M2) shells. It is React-free on purpose so the vertical
 * slice can be tested without a DOM.
 */

export type SheetsClientStatus = 'connecting' | 'ready' | 'error';

export interface SheetsClientState {
  status: SheetsClientStatus;
  /** Selection reported by `host:init` (kept for the status line). */
  selection: SelectionReference | null;
  /** Selection the `ChartDataModel` was built from, when the host reports it. */
  modelSelection: SelectionReference | null;
  model: ChartDataModel | null;
  config: ChartConfig;
  entitlements: Entitlements;
  entitlementSource: EntitlementSource;
  /** Validation/read errors for the current data, or null when healthy. */
  errors: string[] | null;
  /** Non-fatal notices (protocol problems, missing bundles). */
  notices: string[];
  addonVersion: string | null;
  transport: 'iframe' | 'sandbox' | 'direct';
}

export interface SheetsControllerOptions {
  transport: ClientTransport;
  /** Resolved from the shared ChartRegistry by the shell, never imported ad hoc. */
  plugin: IChartPlugin;
  channel: string;
  /** Container the engine renders into. */
  container: HTMLElement;
  clientVersion?: string;
  onState?: (state: SheetsClientState) => void;
  watermarkText?: string;
  fallbackWidth?: number;
  fallbackHeight?: number;
  /** Initial notices (e.g. a malformed host bootstrap). */
  initialNotices?: string[];
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class SheetsClientController {
  private readonly plugin: IChartPlugin;
  private readonly schemas: OptionSchema[];
  private readonly transport: ClientTransport;
  private readonly channel: string;
  private readonly container: HTMLElement;
  private readonly clientVersion: string;
  private readonly onState: ((state: SheetsClientState) => void) | undefined;
  private readonly watermarkText: string;
  private readonly fallbackWidth: number;
  private readonly fallbackHeight: number;
  private stopListening: (() => void) | null = null;
  private state: SheetsClientState;

  constructor(options: SheetsControllerOptions) {
    this.plugin = options.plugin;
    this.schemas = options.plugin.getOptionSchemas();
    this.transport = options.transport;
    this.channel = options.channel;
    this.container = options.container;
    this.clientVersion = options.clientVersion ?? 'dev';
    this.onState = options.onState;
    this.watermarkText = options.watermarkText ?? 'Charts by Nex Flow Automation Trial';
    this.fallbackWidth = options.fallbackWidth ?? 640;
    this.fallbackHeight = options.fallbackHeight ?? 400;
    this.state = {
      status: 'connecting',
      selection: null,
      modelSelection: null,
      model: null,
      config: createDefaultConfig(this.schemas),
      entitlements: { plan: 'trial' },
      entitlementSource: 'stub',
      errors: null,
      notices: options.initialNotices ? [...options.initialNotices] : [],
      addonVersion: null,
      transport: 'direct'
    };
    this.emit();
  }

  getState(): SheetsClientState {
    return this.state;
  }

  /** Subscribe to host messages and announce readiness. */
  start(): void {
    this.stopListening = this.transport.listen((value, origin) => {
      this.handleInbound(value, origin);
    });
    this.send('client:ready', {
      clientVersion: this.clientVersion,
      protocolVersion: PROTOCOL_VERSION
    });
  }

  /** Stop listening (component unmount). */
  destroy(): void {
    this.stopListening?.();
    this.stopListening = null;
  }

  /**
   * Validate and handle one host message.
   *
   * The client re-validates everything it receives: the sidebar relay is a
   * validating relay too, but the client is the endpoint that runs the engine,
   * so it must not depend on another component having done the check.
   */
  handleInbound(value: unknown, origin?: string): BridgeMessage | null {
    let message: BridgeMessage;
    try {
      message = parseMessage(value, {
        direction: 'host-to-client',
        channel: this.channel
      });
    } catch (error) {
      const payload = protocolErrorPayload(error);
      this.pushNotice(`${payload.code}: ${payload.message}`);
      this.reportClientError(payload.code, payload.message, origin);
      return null;
    }

    try {
      switch (message.kind) {
        case 'host:init':
          this.applyInit(message.payload);
          break;
        case 'host:model':
          this.applyModel(message.payload.model, message.payload.selection);
          break;
        case 'host:config':
          this.applyHostConfig(message.payload.config);
          break;
        case 'host:entitlement':
          this.setState({
            entitlements: message.payload.entitlements,
            entitlementSource: message.payload.source
          });
          this.renderSafely();
          break;
        case 'host:error':
          this.setState({
            errors: [message.payload.message],
            status: 'error'
          });
          break;
        default:
          break;
      }
    } catch (error) {
      const message_ = errorMessage(error);
      this.setState({ status: 'error', errors: [message_] });
    }
    return message;
  }

  /**
   * Validate and dispatch a host message that arrived without an origin.
   *
   * Used by the `google.script.run` transport (which polls) and by tests; the
   * `postMessage` transport calls {@link handleInbound} with the event origin.
   */
  emitInbound(value: unknown): BridgeMessage | null {
    return this.handleInbound(value, undefined);
  }

  /**
   * Apply a settings change produced by the shared ui-kit.
   *
   * The new config is validated by the engine (`updateConfig`) before it is used
   * or sent, so the host only ever persists schema-valid values.
   */
  applyConfig(config: ChartConfig): void {
    const changed = changedOptionIds(this.state.config, config);
    this.setState({ config });
    this.renderSafely();
    this.send('client:config:change', { config, changedOptionIds: changed });
  }

  /** Re-resolve a single option through the engine (used by the settings pane). */
  updateOption(schema: OptionSchema, value: unknown): void {
    // `updateConfig` is the engine's single validation path: invalid values
    // throw here and never reach the host or the renderer.
    this.applyConfig(updateConfig(this.state.config, schema, value));
  }

  /** Ask the host to re-read the current selection. */
  refresh(explicit = true): void {
    this.send('client:refresh', { explicit });
  }

  /** Send the live SVG to the host (host-side export/download is M4+). */
  exportSvg(): void {
    const svg = this.container.querySelector('svg');
    this.send('client:export', {
      format: 'svg',
      data: svg ? svg.outerHTML : null
    });
  }

  /* ---------------------------------------------------------------- */

  private applyInit(payload: HostInitPayload): void {
    const config = this.mergeConfig(payload.config);
    this.setState({
      status: 'ready',
      selection: payload.selection,
      config,
      entitlements: payload.entitlements,
      entitlementSource: payload.entitlementSource,
      addonVersion: payload.addonVersion,
      transport: payload.transport.transport
    });
    // `host:init` is always followed by `host:model`, but rendering here keeps
    // the chart in sync when a host sends only the init payload.
    this.renderSafely();
  }

  private applyModel(model: ChartDataModel, selection: SelectionReference | null): void {
    const current = this.state;
    this.setState({
      model,
      // A `host:model` may omit the selection; keep whatever `host:init`
      // reported so the status line does not flicker to "no range selected".
      selection: selection ?? current.selection,
      modelSelection: selection ?? current.modelSelection,
      status: 'ready'
    });
    this.renderSafely();
  }

  private applyHostConfig(config: ChartConfig): void {
    // The host echoes the config it persisted; merge rather than replace so a
    // concurrent local edit is not silently reverted mid-edit.
    const merged = this.mergeConfig(config);
    this.setState({ config: merged });
    this.renderSafely();
  }

  /**
   * Merge persisted values over engine defaults.
   *
   * The host stores an opaque `ChartConfig`; defaults and validation always come
   * from `OptionSchema`, so a stale or removed option simply has no effect.
   */
  private mergeConfig(persisted: ChartConfig | null): ChartConfig {
    const defaults = createDefaultConfig(this.schemas);
    if (!persisted) {
      return defaults;
    }
    const merged = cloneConfig(defaults);
    for (const group of Object.keys(merged) as (keyof ChartConfig)[]) {
      const stored = persisted[group];
      if (stored && typeof stored === 'object') {
        merged[group] = { ...merged[group], ...stored };
      }
    }
    return merged;
  }

  private renderSafely(): void {
    const model = this.state.model;
    if (!model) {
      return;
    }
    try {
      this.render(model);
      if (this.state.errors) {
        this.setState({ status: 'ready', errors: null });
      }
    } catch (error) {
      const errors = error instanceof ChartValidationError ? error.errors : [errorMessage(error)];
      this.setState({ status: 'error', errors });
    }
  }

  private render(model: ChartDataModel): void {
    const ctx: RenderContext = {
      container: this.container,
      width: this.container.clientWidth || this.fallbackWidth,
      height: this.container.clientHeight || this.fallbackHeight,
      // The watermark POLICY is the engine's (capability-driven: `branding`
      // removes it and every paid plan grants it), so the sidebar cannot
      // disagree with Excel or Power BI about when the overlay appears.
      watermark:
        watermarkForEntitlements(this.state.entitlements, { text: this.watermarkText }) ?? undefined
    };
    renderChart(this.plugin, model, this.state.config, ctx);
  }

  private send<K extends ClientToHostKind>(kind: K, payload: PayloadFor<K>): void {
    this.transport.send(createMessage(kind, payload, { channel: this.channel }) as BridgeMessage);
  }

  private reportClientError(code: string, message: string, origin?: string): void {
    this.send('client:error', {
      code,
      message,
      ...(origin !== undefined ? { path: `origin:${origin}` } : {}),
      recoverable: true
    });
  }

  private pushNotice(notice: string): void {
    const notices = [...this.state.notices, notice].slice(-8);
    this.setState({ notices });
  }

  private setState(patch: Partial<SheetsClientState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  private emit(): void {
    this.onState?.(this.state);
  }
}
